import { Component, HostListener, input, output, signal, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

import type { InventoryUnitStatus } from '../../inventory/models/inventory.model';
import { FocusTrap } from '../../../shared/ui/focus-trap';

@Component({
  selector: 'app-status-change-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (isOpen()) {
      <dialog
        class="status-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-status-modal-title"
        aria-describedby="quick-status-modal-description"
        [open]="true"
      >
        <div class="status-modal__header">
          <h2 id="quick-status-modal-title">Cambiar estado de la unidad</h2>
          <p id="quick-status-modal-description">
            Indicá el motivo para cambiar la unidad de {{ currentLabel() }} a {{ nextLabel() }}.
          </p>
        </div>

        <label class="status-modal__field" for="status-change-reason">
          <span>Motivo</span>
          <textarea
            id="status-change-reason"
            [disabled]="isSubmitting()"
            [value]="reasonDraft()"
            (input)="reasonDraft.set($any($event.target).value)"
            [attr.aria-required]="requiresReason()"
          ></textarea>
          @if (!requiresReason()) {
            <small class="status-modal__helper">Solo hace falta si cambiás algún dato de la unidad</small>
          }
          @if (reasonError()) {
            <small class="status-modal__error" role="alert">{{ reasonError() }}</small>
          }
        </label>

        <div class="status-modal__actions">
          <button
            type="button"
            class="app-action app-action--secondary"
            [disabled]="isSubmitting()"
            (click)="onCancel()"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="app-action app-action--primary"
            [disabled]="isSubmitting() || (requiresReason() && !reasonDraft().trim())"
            (click)="onConfirm()"
          >
            Confirmar cambio
          </button>
        </div>
      </dialog>
    }
  `,
  styles: [
    `
      .status-modal {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1000;
        min-width: 360px;
        max-width: 480px;
        padding: 24px;
        border: 1px solid #d3c4bd;
        border-radius: 12px;
        background: #faf6f3;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      }

      .status-modal::backdrop {
        background: rgba(0, 0, 0, 0.4);
      }

      .status-modal__header {
        margin-bottom: 20px;
      }

      .status-modal__header h2 {
        margin: 0 0 8px;
        font-size: 1.1rem;
        color: #332b28;
      }

      .status-modal__header p {
        margin: 0;
        font-size: 0.875rem;
        color: #7a6b66;
      }

      .status-modal__field {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-bottom: 20px;
        font-size: 0.875rem;
        color: #332b28;
      }

      .status-modal__field textarea {
        padding: 10px 12px;
        border: 1px solid #d3c4bd;
        border-radius: 8px;
        background: #ffffff;
        font: inherit;
        resize: vertical;
        min-height: 80px;
      }

      .status-modal__field textarea:focus {
        outline: 2px solid var(--accent);
        outline-offset: 1px;
      }

      .status-modal__field textarea:disabled {
        background: #f5f0ed;
        cursor: not-allowed;
      }

      .status-modal__helper {
        color: #7a6b66;
        font-size: 0.8rem;
      }

      .status-modal__error {
        color: #b91c1c;
        font-size: 0.8rem;
      }

      .status-modal__actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
      }

      .status-modal__actions .app-action {
        min-width: 100px;
      }
    `
  ]
})
export class StatusChangeModalComponent implements OnDestroy {
  readonly visible = input<boolean>(false);
  readonly currentStatus = input<InventoryUnitStatus>('AVAILABLE');
  readonly nextStatus = input<InventoryUnitStatus>('AVAILABLE');
  readonly unitLabel = input<string>('');
  readonly requiresReason = input<boolean>(false);
  readonly isSubmitting = input<boolean>(false);

  readonly confirm = output<string>();
  readonly cancel = output<void>();

  readonly reasonDraft = signal('');
  readonly reasonError = signal<string | null>(null);

  readonly isOpen = () => this.visible();

  readonly currentLabel = () => this.formatStatus(this.currentStatus());
  readonly nextLabel = () => this.formatStatus(this.nextStatus());

  private _focusTrap: FocusTrap | null = null;

  focusConfirmButton(): void {
    const el = document.querySelector('.status-modal') as HTMLElement | null;
    if (!el) return;
    this._focusTrap = new FocusTrap(el);
    this._focusTrap.install();
    // Focus the first interactive element (the textarea)
    const first = el.querySelector<HTMLElement>('textarea, button');
    first?.focus();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible()) {
      this.cancel.emit();
    }
  }

  onConfirm(): void {
    const reason = this.reasonDraft().trim();

    if (this.requiresReason() && !reason) {
      this.reasonError.set('Necesitás un motivo para transiciones hacia o desde MISSING.');
      return;
    }

    this.confirm.emit(reason);
  }

  onCancel(): void {
    this.reasonDraft.set('');
    this.reasonError.set(null);
    this._focusTrap?.uninstall();
    this._focusTrap = null;
    this.cancel.emit();
  }

  ngOnDestroy(): void {
    this._focusTrap?.uninstall();
    this._focusTrap = null;
  }

  private formatStatus(status: InventoryUnitStatus): string {
    switch (status) {
      case 'AVAILABLE':
        return 'Disponible';
      case 'IN_REPAIR':
        return 'En reparación';
      case 'MISSING':
        return 'Faltante';
      case 'SOLD':
        return 'Vendido';
    }
  }
}